import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { InfiosIcon } from '@infios/uikit/react';
import type { ChatMessage } from '../../interfaces/chat';
import { MessageBubble } from '../MessageBubble';
import { StatusIndicator } from '../StatusIndicator';
import styles from './MessageList.module.css';

interface MessageListProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  statusMessage: string | null;
  onRespondHalt: (text: string) => void;
}

/**
 * Scrollable conversation thread. Auto-scrolls to the newest content as
 * messages arrive and while a turn streams, so the user always sees the latest.
 */
export function MessageList({ messages, isStreaming, statusMessage, onRespondHalt }: MessageListProps) {
  const { t } = useTranslation();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [messages, isStreaming, statusMessage]);

  if (messages.length === 0 && !isStreaming) {
    return (
      <div className={styles.thread}>
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>
            <InfiosIcon name="creation" />
          </span>
          <h2 className={styles.emptyTitle}>{t('ChatbotUI.EmptyTitle')}</h2>
          <p className={styles.emptySubtitle}>{t('ChatbotUI.EmptySubtitle')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.thread}>
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          haltDisabled={isStreaming}
          onRespondHalt={onRespondHalt}
        />
      ))}
      {isStreaming ? <StatusIndicator label={statusMessage} /> : null}
      <div ref={endRef} />
    </div>
  );
}
